/* eslint-disable no-restricted-syntax */
/* eslint-disable consistent-return */

'use client';

import { useEffect, useReducer, useState } from 'react';
import type { Key } from 'react-aria';
import { intersects } from '../layouts/get-visible-nodes';
import type { LayoutInfo, Rect } from '../layouts/types';
import { difference } from '../layouts/utils';
import type { ListFilterState } from '../List/types';

export type UseAnimateMutationProps = {
  /** Optionally disable animations completely */
  enabled?: boolean;
  /** max-duration of all transitions in ms */
  duration: number;
  /** Current layout information */
  layoutInfo: Map<Key, LayoutInfo>;
  /** The currently visible rect */
  visibleRect?: Rect;
};

type ReducerState = {
  animating: boolean;
  add: Set<Key>;
  remove: Set<Key>;
  effect?: () => any;
};

type Actions =
  | {
      type: 'start';
      add: Set<Key>;
      remove: Set<Key>;
    }
  | { type: 'stop' };

export function useAnimateMutation<T>(
  { enabled, duration, layoutInfo, visibleRect }: UseAnimateMutationProps,
  { collection }: ListFilterState<T>
) {
  const [state, setAnimating] = useReducer(
    (state: ReducerState, action: Actions) => {
      switch (action.type) {
        case 'start':
          return {
            animating: true,
            add: new Set<Key>([...state.add, ...action.add]),
            remove: new Set<Key>([...state.remove, ...action.remove]),
            effect: () => {
              const timer = setTimeout(() => {
                setAnimating({ type: 'stop' });
              }, duration);
              return () => {
                clearTimeout(timer);
              };
            },
          };
        case 'stop':
          return {
            ...state,
            animating: false,
            add: new Set<Key>(),
            remove: new Set<Key>(),
          };
        default:
          return state;
      }
    },
    {
      animating: false,
      add: new Set<Key>(),
      remove: new Set<Key>(),
    }
  );

  useEffect(() => {
    if (state.effect) {
      // ensure that we only run the effect once
      // by mutating the state and manually deleting
      // the effect
      const effect = state.effect;
      delete state.effect;
      return effect();
    }
  }, [state]);

  const [previous, setPrevious] = useState({
    collection,
    layoutInfo,
  });

  // first check if animations are enabled, and if the current collection
  // is different from the previous collection
  if (enabled && previous.collection !== collection) {
    // we then we partition the difference between the previous layout information
    // and the current layout information to loop through its results
    const { toAdd, toRemove } = difference(previous.layoutInfo, layoutInfo);
    // then it checks if the layout of the current item intersects with the visible
    // rectangle. if it does, it starts the animation by dispatching the 'start' action to the reducer function
    // and breaks the loop
    for (const key of [...toAdd, ...toRemove]) {
      const layout = layoutInfo.get(key) ?? previous.layoutInfo.get(key);
      // @ts-expect-error FIXME(strictNullChecks)
      if (intersects(layout.rect, visibleRect)) {
        setAnimating({
          type: 'start',
          add: toAdd,
          remove: toRemove,
        });
        break;
      }
    }

    // after the loop, it updates the previous state with the current collection and layout information to help
    // keep track of the state for the next render routine
    setPrevious({
      collection,
      layoutInfo,
    });
  }

  return {
    previous,
    animating: state.animating,
    add: state.add,
    remove: state.remove,
  };
}
